import Login from '@/containers/login/login'
import { ReactNode, Suspense, lazy } from 'react'
import { createBrowserRouter } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'

// import { ProtectedRoute } from '@/routes/protected/protectedRoute'
import { Layout } from './layout/layout'
import ServiceDeliveryOverview from '../containers/ServiceDelivery/serviceDeliveryOverview'
import CaseDetails from '../containers/CaseManagement/caseDetails/CaseDetails'
import SessionExpired from '../components/atom/errorPages/sessionExpired/sessionExpired'
import PageNotFound from '../components/atom/errorPages/pageNotFound/pageNotFound'
import { ProtectedRoute } from '@/routes/protected/protectedRoute'
// import SessionExpired from '../components/atom/errorPages/sessionExpired/sessionExpired'
// import SomethingWentWrong from '../components/atom/errorPages/somethingWentWrong/somethingWentWrong'
// import UnAuthorized from '../components/atom/errorPages/unAuthorizedAccess/unAuthorizedAccess'
// import NoDataFound from '../components/atom/errorPages/noDataFound/noDataFound'
// import NoDataFound from '../components/atom/errorPages/noDataFound/noDataFound'
// import UnauthorizedPage from '@/routes/ErrorPage/unauthorizedPage'

const NetworkMapFullscreen = lazy(
  () =>
    import('../containers/Dashboard/NewDashboard/cards/NetworkMapFullscreen'),
)

// Dashboard
const ResponsiveDashboard = lazy(
  () => import('../containers/Dashboard/responsiveDashboard'),
)
const CreateCase = lazy(() => import('../containers/CaseManagement/createCase'))
const CasesTable = lazy(
  () => import('../containers/CaseManagement/casesTable/casesTable'),
)
const CaseManagement = lazy(
  () => import('../containers/CaseManagement/caseManagement'),
)
const UnAuthorized = lazy(
  () =>
    import(
      '../components/atom/errorPages/unAuthorizedAccess/unAuthorizedAccess'
    ),
)
// const NotFound = lazy(() => import('@/routes/notFound/notFound'))
const SomethingWentWrong = lazy(
  () =>
    import(
      '../components/atom/errorPages/somethingWentWrong/somethingWentWrong'
    ),
)

const SiteTable = lazy(
  () => import('../containers/TechnologyAndNetwork/Sites/siteList'),
)
const RecentAlertsExpanded = lazy(
  () =>
    import(
      '../containers/TechnologyAndNetwork/Overview/ActiveAlerts/activeAlertsExpanded'
    ),
)
const DeviceInventory = lazy(
  () =>
    import('../containers/TechnologyAndNetwork/ActiveServices/activeServices'),
)
const NodeDetails = lazy(
  () => import('../containers/TechnologyAndNetwork/ActiveServices/nodeDetails'),
)
const CradlepointNodeDetails = lazy(
  () =>
    import(
      '../containers/TechnologyAndNetwork/ActiveServices/cradlepointNodeDetails'
    ),
)
const ServiceInventory = lazy(
  () =>
    import(
      '../containers/TechnologyAndNetwork/ServiceInventory/serviceInventory'
    ),
)
const NewOverview = lazy(
  () => import('../containers/TechnologyAndNetwork/Overview/newOverview'),
)
const NetworkNodeMapPage = lazy(
  () =>
    import(
      '../containers/TechnologyAndNetwork/Overview/NetworkNodeMap/NetworkNodeMapPage'
    ),
)
const Bills = lazy(() => import('../containers/BillingAndUsage/Bills/Bills'))
const ServiceBillings = lazy(
  () => import('../containers/BillingAndUsage/serviceBillings/serviceBillings'),
)
const Users = lazy(() => import('../containers/UsersAndGroups/users'))
const ViewContact = lazy(
  () => import('../containers/UsersAndGroups/viewContact'),
)
const ServiceDeliverySites = lazy(
  () => import('../containers/ServiceDelivery/serviceDeliverySites'),
)
const OrderManagement = lazy(
  () => import('../containers/ServiceDelivery/Orders/orderManagement'),
)
const Orders = lazy(() => import('../containers/ServiceDelivery/Orders/orders'))
const OrderDetails = lazy(
  () =>
    import('../containers/ServiceDelivery/Orders/views/details/orderDetails'),
)
const ServiceDelivery = lazy(
  () => import('../containers/ServiceDelivery/serviceDelivery'),
)
const Reports = lazy(
  () => import('../containers/AnalyticsAndReports/reports/reports'),
)
const RunReports = lazy(
  () => import('../containers/AnalyticsAndReports/reports/runReports'),
)
const ScheduledReports = lazy(
  () => import('../containers/AnalyticsAndReports/reports/scheduledReports'),
)
const ReportByCategory = lazy(
  () => import('../containers/AnalyticsAndReports/reports/reportByCategory'),
)
const BillingAndUsageOverview = lazy(
  () =>
    import('../containers/BillingAndUsage/overview/billingAndUsageOverview'),
)
const Payments = lazy(
  () => import('../containers/BillingAndUsage/payments/payments'),
)
const Settings = lazy(() => import('../containers/Settings/settings'))
const WikiChatbot = lazy(() => import('../containers/Wiki/WikiChatbot'))

type WrapperProps = {
  children: ReactNode
  height?: string
  width?: string
}

const Placeholder = ({
  height = '100%',
  width = '100%',
}: {
  height?: string
  width?: string
}) => (
  <div
    style={{ height, width }}
    className='animate-pulse rounded-radius-md bg-primary'
  />
)

const LazyLoadWrapper = ({
  children,
  height = '100%',
  width = '100%',
}: WrapperProps) => (
  <Suspense
    fallback={
      <div style={{ height, width }}>
        <Placeholder height={height} width={width} />
      </div>
    }
  >
    {children}
  </Suspense>
)

const withSuspense = (component: ReactNode) => (
  <LazyLoadWrapper>{component}</LazyLoadWrapper>
)

const routes = [
  {
    path: '/',
    element: <Login />,
  },

  {
    element: (
      <ErrorBoundary fallback={<SomethingWentWrong />}>
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      </ErrorBoundary>
    ),

    children: [
      {
        path: 'home',
        element: withSuspense(<ResponsiveDashboard />),
      },
      {
        path: 'home/network-map',
        element: withSuspense(<NetworkMapFullscreen />),
      },
      {
        path: '/settings',
        element: withSuspense(<Settings />),
      },
      {
        path: 'wiki',
        element: withSuspense(<WikiChatbot />),
      },
      {
        path: '/wiki',
        element: withSuspense(<WikiChatbot />),
      },

      // Technology & Networks
      {
        path: '/technologyandnetworks/sitepage',
        element: withSuspense(<NewOverview />),
      },
      {
        path: '/technologyandnetworks/sitepage/network-map',
        element: withSuspense(<NetworkNodeMapPage />),
      },
      {
        path: 'technologyandnetworks/sitelist',
        element: withSuspense(<SiteTable />),
      },
      {
        path: 'technologyandnetworks/activeservices',
        element: withSuspense(<DeviceInventory />),
      },
      {
        path: 'technologyandnetworks/activeservices/api/:serialNumber',
        element: withSuspense(<CradlepointNodeDetails />),
      },
      {
        path: 'technologyandnetworks/activeservices/:nodeIpAddress',
        element: withSuspense(<NodeDetails />),
      },
      {
        path: 'technologyandnetworks/serviceinventory',
        element: withSuspense(<ServiceInventory />),
      },
      {
        path: '/technologyandnetworks/sitepage/alerts',
        element: withSuspense(<RecentAlertsExpanded />),
      },

      // Users
      {
        path: 'userandgroups/users',
        element: withSuspense(<Users />),
      },
      {
        path: 'userandgroups/users/:contactId',
        element: withSuspense(<ViewContact />),
      },

      // Support
      {
        path: 'support/casemanagement',
        element: withSuspense(<CaseManagement />),
        key: 'casemanagement',
        children: [
          {
            index: true,
            element: withSuspense(<CasesTable />),
            key: 'case-table',
          },
          {
            path: 'create',
            element: <CreateCase />,
            key: 'create-case',
          },
          {
            path: ':caseId',
            element: <CaseDetails />,
            key: 'case-detail',
          },
        ],
      },
      // Billing & Usage
      {
        path: 'billingandusage/bills',
        element: withSuspense(<Bills />),
      },
      {
        path: 'billingandusage/servicebilling',
        element: withSuspense(<ServiceBillings />),
      },
      {
        path: 'billingandusage/overview',
        element: withSuspense(<BillingAndUsageOverview />),
      },
      {
        path: 'billingandusage/payments',
        element: withSuspense(<Payments />),
      },
      // Analytics & Reports
      {
        path: 'analyticsandreports/reports',
        element: withSuspense(<Reports />),
        children: [
          {
            index: true,
            element: withSuspense(<RunReports />),
          },
          {
            path: 'scheduledreports',
            element: withSuspense(<ScheduledReports />),
          },
          {
            path: ':item',
            element: withSuspense(<ReportByCategory />),
          },
          {
            path: ':item/statement/:id',
            element: withSuspense(<ReportByCategory />),
          },
        ],
      },

      // Service Delivery
      {
        path: 'service-delivery',
        element: withSuspense(<ServiceDelivery />),
        children: [
          {
            path: 'overview',
            index: true,
            element: withSuspense(<ServiceDeliveryOverview />),
          },
          {
            path: 'orders',
            element: withSuspense(<OrderManagement />),
            children: [
              {
                index: true,
                element: withSuspense(<Orders />),
              },
              {
                path: ':orderId',
                element: withSuspense(<OrderDetails />),
              },
            ],
          },
          {
            path: 'sites',
            element: withSuspense(<ServiceDeliverySites />),
          },
        ],
      },
    ],
  },

  // Unauthorized
  {
    path: '/unauthorized',
    element: withSuspense(<UnAuthorized />),
  },

  {
    path: '/expired',
    element: withSuspense(<SessionExpired />),
  },
  // 404
  {
    path: '*',
    element: withSuspense(<PageNotFound />),
  },
]

export const router = createBrowserRouter(routes, {
  basename: '/app',
  future: {
    v7_relativeSplatPath: true,
  },
})
